import jwtDecode, { JwtPayload } from 'jwt-decode'
import lodash from 'lodash'
import moment from 'moment'
import { hash } from 'ohash'
import $global from '@xt/client/store/global'
import singleServiceTime from '@xt/client/experimental/server-time'
import { EnumApiUser } from '@xt/client/api'
import { $service } from './service'
import { reportEntryLog, reportSumLog } from '@xt/client/utils/log'
import { BackupKeys } from '@xt/client/entities/user/consts'
import { AxiosRequestConfig } from 'axios'
import { OAuthRequestError, Result } from './types.d'
import { EnumRequestError } from './interceptors'
import { isOAuthRequestError } from './utils'
import { OAuthError } from '@xt/client/entities/user/controller'
import { refreshMemoryUserInfo } from '@xt/client/experimental/refresh-memory-userinfo'

export type TokenType = {
  access_token: string
  token_type: 'bearer'
  refresh_token: string
  expires_in: number
  scope: 'all'
  dev_id: string
  appid: string
  applet_auth: number | null
  applet_embed: number | null
  auth_type: string
  jti: string
  login_time?: number
}

export const TokenKey = `${$global.localStorageStartsWith}Token`
export const BasicToken = 'Basic dXNlci1jbGllbnQ6dXNlci1zZWNyZXQtODg4OA=='
export const TokenFields = [
  'access_token',
  'token_type',
  'refresh_token',
  'expires_in',
  'scope',
  'dev_id',
  'appid',
  'applet_auth',
  'applet_embed',
  'auth_type',
  'jti',
  'login_time'
]

class SingleToken {
  private static instance: SingleToken | null = null
  private tokenData: TokenType | null = null
  private promiseTask: Promise<Result<TokenType>> | null = null

  constructor() {}

  static getInstance() {
    if (!SingleToken.instance) {
      SingleToken.instance = new SingleToken()
    }
    return SingleToken.instance
  }

  get jwtPayload() {
    const token = this.getToken()
    if (token) {
      return jwtDecode<
        JwtPayload & {
          authorities: Array<String>
          client_id: string
          user_name: string
          auth_type: string | null
          applet_auth: string | null
        }
      >(token.access_token)
    }
  }

  get expirationTime() {
    const exp = lodash.get(this.jwtPayload, 'exp', 0)
    if (exp) {
      return moment(exp * 1000)
    }
    return singleServiceTime.serviceDate.utcOffset(+8)
  }

  setToken(token: TokenType) {
    if (!token) return

    reportEntryLog('debug.single-token.set', {
      rt: 'refresh_token' in token,
      at: 'access_token' in token
    })

    // 记录登录时间点
    token.login_time = Date.now()
    this.tokenData = token
    localStorage.setItem(TokenKey, JSON.stringify(token))

    this.backToken(token)
  }

  private backToken(token: TokenType) {
    if ($global.platform === 'Mobile') {
      switch (sessionStorage.getItem('__xt__embedded__source')) {
        case 'uc':
          localStorage.setItem(BackupKeys.token_uc_backup, JSON.stringify(token))
          break
        case 'show':
          localStorage.setItem(BackupKeys.token_show_backup, JSON.stringify(token))
          break
        default:
          localStorage.setItem(BackupKeys.token_h5_backup, JSON.stringify(token))
          break
      }
    }
  }

  getToken(): TokenType | null {
    try {
      return this.tokenData || JSON.parse(localStorage.getItem(TokenKey) || 'null')
    } catch {
      return null
    }
  }

  removeToken() {
    this.tokenData = null
    localStorage.removeItem(TokenKey)
  }

  private refreshTokenAssist(request?: AxiosRequestConfig): Promise<Result<TokenType>> {
    const token = this.getToken()!
    if (!this.promiseTask) {
      const auth_type = this.jwtPayload?.auth_type ?? ''
      const applet_auth = this.jwtPayload?.applet_auth ?? ''

      const headers = lodash.assign(
        {},
        $global.isEmbeddedH5Page
          ? {
              'x-appletEmbed': '1',
              'x-authType': '2'
            }
          : { 'x-authType': auth_type, 'x-appletAuth': applet_auth },
        request?.headers ?? {}
      )

      // 传递url：被动刷新token 上报日志
      if (request) {
        try {
          const refresh_token = token.refresh_token
          const access_token = token.access_token
          const serverTime = singleServiceTime.serviceDate.valueOf()
          const minutes = this.expirationTime.diff(serverTime, 'minutes')
          reportEntryLog('debug.refresh-token', {
            minutes,
            exp: lodash.get(this.jwtPayload, 'exp', 0),
            url: request.url,
            clientTime: Date.now(),
            headers,
            serverTime,
            refresh_hash: hash(refresh_token),
            access_hash: hash(access_token)
          })
        } catch {}
      }

      this.promiseTask = $service<TokenType>({
        url: `${EnumApiUser.Login}?grant_type={grant_type}&refresh_token={refresh_token}`,
        method: 'post',
        headers: {
          Authorization: 'Basic dXNlci1jbGllbnQ6dXNlci1zZWNyZXQtODg4OA==',
          ...headers
        },
        data: {
          grant_type: 'refresh_token',
          refresh_token: token.refresh_token,
          // 使用完参数清理 body
          delete: true
        }
      }).finally(() => {
        this.promiseTask = null
      })
    }

    return this.promiseTask
  }

  async refreshToken(request?: AxiosRequestConfig) {
    // 当前内存的tokenData和localStorage是否一致，如果不一致可能当前localStorage是最新的，可以直接使用
    const localToken = localStorage.getItem(TokenKey)
    if (this.tokenData && localToken) {
      try {
        const { access_token } = JSON.parse(localToken)
        if (access_token !== this.tokenData.access_token) {
          this.setToken(JSON.parse(localToken))
          reportSumLog('app.store-storage.sync')
          console.warn('同步内存中token数据')
          return this.tokenData
        }
      } catch (e) {
        reportEntryLog('app.store-storage.sync-error', { e })
      }
    }

    const token = this.getToken()
    if (token && token.refresh_token) {
      const [error, data] = await this.refreshTokenAssist(request)
      if (error || !data.access_token) {
        reportEntryLog('debug.refresh-token.error', {
          type: error !== null ? '0' : '1',
          error,
          data
        })

        // 此处单独处理一下设备认证失败场景 无需应用刷新token失败降级逻辑
        if (data && 'errCode' in data && data.errCode === OAuthError.UnknowAuthDevice) {
          // 移除用户信息
          window.$nuxt.$store.$storeUser.clearUserInfo()
          // 移除用户token
          this.removeToken()
        }

        throw { errCode: EnumRequestError.XHR_API_FAILED, errMsg: 'refresh token失败' }
      }

      this.setToken(data)

      try {
        await window.$nuxt.$store.$storeUser.onSaveDeviceInfo()
      } catch {}
      return data
    }

    return null
  }

  /**
   * 仅用于暄桐小程序授权pc轮询最后一步登录和暄桐小程序授权微信内h5轮询最后一步登录两种场景使用 ！
   * @param tempToken 
   * @param headers 
   * @returns 
   */
  async onLoginByTempToken(tempToken: string, headers?: Record<string, any>) {
    const requestHeader = headers ?? {}

    let [error, data] = await $service<TokenType | OAuthRequestError>({
      url: `${EnumApiUser.Login}?grant_type={grant_type}&tempToken={tempToken}`,
      method: 'post',
      headers: {
        Authorization: 'Basic dXNlci1jbGllbnQ6dXNlci1zZWNyZXQtODg4OA==',
        ...requestHeader
      },
      data: {
        grant_type: 'temp_token',
        tempToken,
        // 使用完参数清理 body
        delete: true
      }
    })

    if (error) {
      reportEntryLog('debug.token.login-by-refresh-error', { error, type: typeof error, empty: error == null })
      throw { errCode: EnumRequestError.XHR_API_FAILED, errMsg: 'by refreshToken login fail' }
    }

    const userStore = window.$nuxt.$store.$storeUser
    if (isOAuthRequestError(data)) {
      userStore.clearUserInfo()
      if (data.errCode === OAuthError.UnknownDevice) {
        data = (await userStore.onAuthPCBindDevice(headers)) as TokenType
      } else if (data.errCode === OAuthError.UnknowAuthDevice) {
        data = (await userStore.onAuthH5BindDevice(headers)) as TokenType
      }
    }

    if (data) {
      this.setToken(data as TokenType)

      try {
        await userStore.onSaveDeviceInfo()
        await userStore.onUpdatingUserInfo()

        reportEntryLog('debug.token.login-by-refresh-success', {})
        // 通知一下其他tab刷新头像
        refreshMemoryUserInfo.trigger()
      } catch {}
    }

    return data
  }

  /**
   * 上报token调试参数
   */
  reportLog() {
    const userItem = localStorage.getItem(BackupKeys.userinfo)
    const tokenItem = localStorage.getItem(`${$global.localStorageStartsWith}Token`)

    reportEntryLog('debug.single-token.log', {
      memery: this.tokenData === null,
      tokenType: typeof tokenItem,
      emptyToken: tokenItem === null || tokenItem === '',
      userType: typeof userItem,
      emptyUser: userItem === null || userItem === ''
    })
  }
}

export default SingleToken.getInstance()
